import { Button, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import { View } from "react-native";
import { Plan } from "./utils";
import Card from "./PlanCardComponent";
import styled from "styled-components/native";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { VDBIcons } from "@utils/VDBIcons";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";
import ContactUsModal from "@components/molecules/ContactUs";
import { DefaultTheme } from "styled-components/native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { getDaysLeft } from "@scenes/more/MoreScreen/utils";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";

const ManagePlanStep1 = ({
  plansData,
  selectedPlanIndex,
  handleSelectPlan,
  additionalUsers,
  setAdditionalUsers,
  selectedPlan,
  t,
  typography,
  theme,
  trackButtonEvent,
  setShowContactUsModal,
  showContactUsModal,
  setShowCancelRenewalPopup,
  setStep,
  disableReviewButton,
  type,
  setUserId,
}: {
  plansData: any;
  selectedPlanIndex: number;
  handleSelectPlan: (index: number) => void;
  additionalUsers: number;
  setAdditionalUsers: (users: number) => void;
  selectedPlan: Plan;
  t: any;
  typography: any;
  theme: any;
  trackButtonEvent: (
    event: string,
    component: string,
    data: any,
    view: string
  ) => void;
  setShowContactUsModal: (show: boolean) => void;
  showContactUsModal: boolean;
  setShowCancelRenewalPopup: (show: boolean) => void;
  setStep: (step: number) => void;
  disableReviewButton: boolean;
  type: "renewal" | "purchase";
  setUserId: (id: number) => void;
}) => {
  const userInfo = useUserInfo();
  return (
    <Overlay>
      {type === "renewal" ? (
        <>
          <VDBText
            type={typography["Body-R"]}
            color={theme["core-06"]}
            textAlign="center"
          >
            {t(
              "managePlan.popUp.renewal.description.text",
              "Choose the plan and number of additional users for your upcoming renewal. "
            )}
          </VDBText>
          <Spacer size={SpaceType.Big} />
          <WarningBox>
            <WarningBoxRow>
              <VDBIcon
                color={theme["warn-01"]}
                size={24}
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.WARNING}
              />
              <Spacer size={SpaceType.Basic} />
              <WarningBoxTextContainer>
                <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                  {userInfo.getUpcomingSubscription()?.end_date
                    ? t(
                        "managePlan.popUp.renewal.warning.title.text",
                        "Changes will take effect on {{renewal_date}}.",
                        {
                          renewal_date: getDaysLeft(
                            userInfo.planDetails()?.current_plan_expiry
                              ?.expiry_date
                          )?.renewalDate,
                        }
                      )
                    : t(
                        "managePlan.popUp.renewal.warning.title.text",
                        "Changes will take effect on your renewal date."
                      )}
                </VDBText>
                <Spacer size={SpaceType.ExtraSmall} />
                <VDBText type={typography["Body-R"]} color={theme["core-05"]}>
                  {t(
                    "managePlan.popUp.renewal.warning.description.text",
                    "Your current access and plan will remain unchanged until then."
                  )}
                </VDBText>
              </WarningBoxTextContainer>
            </WarningBoxRow>
          </WarningBox>
        </>
      ) : (
        <>
          <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
            {t("managePlan.popUp.billing.title.text", "Billing Term")}
          </VDBText>
          <Spacer size={SpaceType.ExtraSmall} />
          <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
            {t(
              "managePlan.popUp.billing.description.text",
              "Billing will begin after the trial period ends"
            )}
          </VDBText>
        </>
      )}
      <Spacer size={SpaceType.Big} />
      <PlanList isMobile={theme.isMobile}>
        {plansData?.plans?.map((item: Plan, index: number) => (
          <View
            key={item.plan_id}
            style={theme.isMobile ? undefined : { flex: 1 }}
          >
            <Card
              title={item.label}
              description={t(
                "managePlan.popUp.promo.text",
                "Billed upfront for 2 years"
              )}
              checked={selectedPlanIndex === index}
              onSelect={() => handleSelectPlan(index)}
              item={item as Plan}
              additionalUsers={additionalUsers}
              setAdditionalUsers={setAdditionalUsers}
              disabled={item.plan_id === selectedPlan?.plan_id}
              type={type}
            />
          </View>
        ))}
      </PlanList>
      {type === "purchase" ? (
        <>
          {!theme.isMobile && <Spacer size={SpaceType.Big} />}
          <VDBText
            type={typography["Body-R"]}
            color={theme["core-06"]}
            textAlign="center"
          >
            {t(
              "managePlan.popUp.billing.screenText.text",
              "Locked users and pricing for 1 year. All users share one billing cycle. \n Additional users are prorated to match your plan term."
            )}
          </VDBText>
        </>
      ) : null}
      <Spacer size={SpaceType.Big} />
      <Button
        title={t("managePlan.popUp.billing.button.text", "Continue")}
        category={ButtonCategory.default}
        type={ButtonType.primary}
        onPress={() => {
          setStep(2);
        }}
        iconSource={VDBIcons.RIGHT}
        {...(!(selectedPlanIndex === null || disableReviewButton) && {
          iconColor: theme["primary-cont"],
        })}
        iconSize={24}
        showIcon
        disabled={selectedPlanIndex === null || disableReviewButton}
      />
      <Spacer size={SpaceType.Big} />
      <ContactUsRow
        justifyContent={type === "renewal" ? "space-between" : "center"}
      >
        {type === "renewal" ? (
          <VDBText
            type={typography["Body-M"]}
            color={theme["link-static"]}
            onPress={() => {
              if (userInfo.getUpcomingSubscription()?.subscription_id) {
                setShowCancelRenewalPopup(true);
              } else {
                showToast(
                  undefined,
                  t(
                    "managePlan.popUp.renewal.previousRequestInProgress.error.text",
                    "Previous request may be in progress. Please try again in sometime."
                  ),
                  ToastTypes.ERROR,
                  true,
                  true
                );
                setUserId(userInfo?.user?.id);
              }
            }}
          >
            {t("managePlan.popUp.cancelRenewal.text", "Cancel Renewal")}
          </VDBText>
        ) : null}
        <ContactUsContainer>
          <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
            {t("managePlan.popUp.contactUs.text", "Need help?")}{" "}
          </VDBText>
          <VDBText
            type={typography["Body-M"]}
            color={theme["link-static"]}
            onPress={() => {
              trackButtonEvent(
                EventName.BUTTON_CLICK,
                EventType.CONTACT_US_BUTTON_CLICK,
                { component_type: ViewType.POP_UP },
                ViewTypeEnum?.MANAGE_PLAN_POP_UP
              );
              setShowContactUsModal(true);
            }}
          >
            {t("contactUs.title.text")}
          </VDBText>
        </ContactUsContainer>
        <ContactUsModal
          isOpen={showContactUsModal}
          onClose={() => setShowContactUsModal(false)}
          screenName="Manage Plan"
        />
      </ContactUsRow>
    </Overlay>
  );
};

const Overlay = styled.View`
  padding-vertical: ${SpaceType.Big}px;
  padding-horizontal: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? SpaceType.Basic : SpaceType.Big}px;
  align-items: center;
`;

const PlanList = styled.View<{ isMobile: boolean }>`
  width: 100%;
  flex-direction: ${({ isMobile }: { isMobile: boolean }) =>
    isMobile ? "column" : "row"};
  gap: ${({ isMobile }: { isMobile: boolean }) =>
    isMobile ? SpaceType.Double : SpaceType.Big}px;
`;

const ContactUsContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const WarningBox = styled.View`
  width: 100%;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["warn-02"]};
  padding: ${SpaceType.Basic}px;
  border-radius: ${SpaceType.Basic}px;
`;

const WarningBoxRow = styled.View`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const WarningBoxTextContainer = styled.View`
  flex: 1;
  min-width: 0;
`;

const ContactUsRow = styled.View<{
  justifyContent: "space-between" | "center";
}>`
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: ${({
    justifyContent,
  }: {
    justifyContent: "space-between" | "center";
  }) => justifyContent};
`;

export default ManagePlanStep1;
